/**
 * Moving and zooming the station map, as on the old kiosk: one finger drags, two fingers pinch, the
 * wheel zooms where the pointer is, buttons zoom in and out and fit it again. The view is a translate
 * and a scale of the drawing inside its frame (canvas px); the maths is pure (tested), the hook only
 * turns pointers into calls.
 */
import { useRef, useState, type PointerEvent, type WheelEvent } from 'react';

export interface View {
  scale: number;
  x: number;
  y: number;
}

export const FIT: View = { scale: 1, x: 0, y: 0 };
export const MIN_SCALE = 1;
export const MAX_SCALE = 4;

/** Keep the drawing over its frame: never dragged away, never smaller than the frame. */
export function clampView(v: View, width: number, height: number): View {
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale));
  const minX = width - width * scale;
  const minY = height - height * scale;
  return { scale, x: Math.min(0, Math.max(minX, v.x)), y: Math.min(0, Math.max(minY, v.y)) };
}

/** Zoom by `factor` keeping the point (px, py) of the frame where it is. */
export function zoomAt(v: View, factor: number, px: number, py: number, width: number, height: number): View {
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale * factor));
  const k = scale / v.scale;
  return clampView({ scale, x: px - (px - v.x) * k, y: py - (py - v.y) * k }, width, height);
}

export function panBy(v: View, dx: number, dy: number, width: number, height: number): View {
  return clampView({ ...v, x: v.x + dx, y: v.y + dy }, width, height);
}

/** A drag longer than this is a move, not a tap on a point. */
const TAP_SLOP_PX = 8;

export function usePanZoom() {
  const frame = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>(FIT);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const moved = useRef(0);

  /** The frame's size in canvas px, and how many screen px make one (the canvas is scaled to fit). */
  const measure = () => {
    const el = frame.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const k = el.offsetWidth ? rect.width / el.offsetWidth : 1;
    return { width: el.offsetWidth, height: el.offsetHeight, left: rect.left, top: rect.top, k };
  };

  const onPointerDown = (e: PointerEvent) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) moved.current = 0;
  };

  const onPointerMove = (e: PointerEvent) => {
    const before = pointers.current.get(e.pointerId);
    const m = measure();
    if (!before || !m) return;
    if (pointers.current.size === 1) {
      const dx = (e.clientX - before.x) / m.k;
      const dy = (e.clientY - before.y) / m.k;
      moved.current += Math.abs(dx) + Math.abs(dy);
      setView((v) => panBy(v, dx, dy, m.width, m.height));
      // capture only once it is a drag (a tap still reaches the point under the finger); capturing is
      // a nicety: it throws for a pointer already gone, and must never stop the drag
      if (moved.current > TAP_SLOP_PX) {
        try {
          (e.currentTarget as Element).setPointerCapture(e.pointerId);
        } catch { /* the pointer is gone: nothing to capture */ }
      }
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.entries()].map(([id, p]) => (id === e.pointerId ? { x: e.clientX, y: e.clientY } : p)) as [{ x: number; y: number }, { x: number; y: number }];
      const [a0, b0] = [...pointers.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const dist0 = Math.hypot(a0.x - b0.x, a0.y - b0.y) || dist;
      const mid = { x: ((a.x + b.x) / 2 - m.left) / m.k, y: ((a.y + b.y) / 2 - m.top) / m.k };
      moved.current += TAP_SLOP_PX + 1;
      setView((v) => zoomAt(v, dist / dist0, mid.x, mid.y, m.width, m.height));
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
  };

  const onPointerUp = (e: PointerEvent) => {
    pointers.current.delete(e.pointerId);
  };

  const onWheel = (e: WheelEvent) => {
    const m = measure();
    if (!m) return;
    setView((v) => zoomAt(v, Math.exp(-e.deltaY * 0.0015), (e.clientX - m.left) / m.k, (e.clientY - m.top) / m.k, m.width, m.height));
  };

  /** A tap after a drag is not a tap on a point. */
  const onClickCapture = (e: { stopPropagation(): void; preventDefault(): void }) => {
    if (moved.current > TAP_SLOP_PX) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const zoomBy = (factor: number) => {
    const m = measure();
    if (m) setView((v) => zoomAt(v, factor, m.width / 2, m.height / 2, m.width, m.height));
  };

  return {
    frame,
    view,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onWheel, onClickCapture },
    zoomIn: () => zoomBy(1.4),
    zoomOut: () => zoomBy(1 / 1.4),
    fit: () => setView(FIT),
  };
}
